import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, RotateCcw, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Btn, Panel } from "@/components/portal/crm/kit";
import { PackButton } from "@/components/portal/crm/pitch/DealPitch";
import { useHubPitch } from "@/lib/dealPitch";
import { ukDayTime } from "@/lib/time";
import { PitchDocs, PitchFields } from "./PitchFields";
import "@/styles/deal-pitch.css";

const STATUS = {
  none: "Not started", drafting: "Connie is drafting…", draft: "Draft — not on any desk yet",
  approved: "Approved — on lender desks", failed: "Draft didn't finish",
};

function Head({ d, busy, onDraft, onApprove, onPreview, testId }) {
  const has = !!d.content;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
      <span className={`dp-status s-${d.status}`} data-testid={`${testId}-status`}>{STATUS[d.status] || d.status}</span>
      {has && d.status !== "drafting" && <PackButton onPack={onPreview} testId={`${testId}-preview`} label="Preview the pack" />}
      {d.status !== "drafting" && (
        <Btn sm onClick={onDraft} disabled={busy} testId={`${testId}-draft`}>
          {has ? <RotateCcw className="size-3.5" /> : <Sparkles className="size-3.5" />}{has ? "Redraft with Connie" : "Ask Connie to draft it"}
        </Btn>
      )}
      {has && d.status === "draft" && (
        <Btn kind="primary" sm onClick={() => onApprove(true)} disabled={busy} testId={`${testId}-approve`}>
          <CheckCircle2 className="size-3.5" /> Approve for lender desks
        </Btn>
      )}
      {d.status === "approved" && <Btn sm onClick={() => onApprove(false)} disabled={busy} testId={`${testId}-unapprove`}>Take it back</Btn>}
    </div>
  );
}

/** The Hub's deal pitch: Connie drafts, the desk edits and approves, and only then do lender desks see it. */
export function PitchEditor({ leadId, company, reference, onStatus, testId = "hub-pitch" }) {
  const { data: d, draft, save, approve, preview } = useHubPitch(leadId);
  const [form, setForm] = useState(null);
  const [files, setFiles] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (d && !dirty) { setForm(d.content ? { ...d.content } : null); setFiles(d.files || []); }
  }, [d, dirty]);
  useEffect(() => { if (d && onStatus) onStatus(d.status === "draft" && !!d.auto); }, [d, onStatus]);
  if (!d) return <Panel testId={testId}><p className="dp-fine">Opening the pitch…</p></Panel>;

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't work — try again in a moment"); }
    finally { setBusy(false); }
  };
  const onDraft = () => {
    if (d.content && !window.confirm("Connie will write a fresh draft over this one, and any approval comes off. Carry on?")) return;
    setDirty(false);
    run(draft, "Connie is on it — usually under a minute");
  };
  const onSave = () => run(async () => { await save({ ...form, files }); setDirty(false); }, "Pitch saved");
  const onApprove = (on) => run(async () => {
    if (dirty) { await save({ ...form, files }); setDirty(false); }
    await approve(on);
  }, on ? "Approved — lender desks on this case can see it now" : "Taken back — desks no longer see it");
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  return (
    <>
      <Panel title="Deal pitch" testId={testId}
             sub="Connie writes it from the case, the company check and the documents. No lender desk sees it until you approve it — then it's a deal pack they can send to their credit team or lender panel."
             right={<Head d={d} busy={busy} onDraft={onDraft} onApprove={onApprove} onPreview={() => preview(company, reference)} testId={testId} />}>
        {d.status === "drafting" && (
          <div className="dp-drafting" data-testid={`${testId}-drafting`}>
            <Loader2 className="size-4 animate-spin" /> Connie is reading the case, the company check and the documents…
          </div>
        )}
        {d.auto && d.status === "draft" && (
          <p className="dp-fine" style={{ margin: "0 0 14px", color: "var(--accent-text)", fontWeight: 600 }} data-testid={`${testId}-auto`}>
            Connie drafted this automatically when the case went to its first lender. Check it over, then approve it for the desks.
          </p>
        )}
        {d.error && d.status !== "drafting" && <p className="dp-fine" style={{ color: "var(--s-dec-tx)" }} data-testid={`${testId}-error`}>{d.error}</p>}
        {!d.content && d.status !== "drafting" && (
          <p className="dp-fine" style={{ margin: 0 }}>
            {d.has_check ? "The company check is in, so Connie has the public record to work from." : "There's no company check on this case yet — Connie will work from the case and its documents."}
          </p>
        )}
        {form && d.status !== "drafting" && (
          <>
            <PitchFields form={form} set={set} testId={testId} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginTop: 18 }}>
              <Btn kind="ink" sm onClick={onSave} disabled={busy || !dirty} testId={`${testId}-save`}>
                <Save className="size-3.5" /> {dirty ? "Save changes" : "Saved"}
              </Btn>
              <span className="dp-fine" style={{ margin: 0 }}>
                {d.drafted_at && `Drafted by Connie ${ukDayTime(d.drafted_at)}`}
                {d.updated_by && ` · edited by ${d.updated_by}`}
                {d.approved_at && ` · approved by ${d.approved_by} ${ukDayTime(d.approved_at)}`}
              </span>
            </div>
          </>
        )}
      </Panel>
      {form && d.status !== "drafting" && (
        <>
          <div style={{ height: 16 }} />
          <Panel title="Documents in the pack" sub="Ticked documents go at the back of the deal pack, after the pitch and the company check."
                 testId={`${testId}-docs-panel`}>
            <PitchDocs available={d.available || []} chosen={files} onChange={(v) => { setFiles(v); setDirty(true); }} testId={`${testId}-docs`} />
          </Panel>
        </>
      )}
    </>
  );
}
